import React, { useState } from "react";

import SubmissionButton from "./submissionButton";
import SubmissionModal from "./SubmissionModal";
import SubmissionsForum from "../../SubmissionsForum/SubmissionsForum";

import TransitionModal from "./TransitionModal";
import FeedbackModal from "./Feedback/FeedbackModal";

import { motion, AnimatePresence } from "framer-motion";
import { UserPlus2, X, CalendarDays, Clock3, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Avatar = ({ name, src }) => {
  const initials = (name || "You")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const isValidSrc =
    src &&
    typeof src === "string" &&
    src.trim().length > 5 &&
    !/^https?:\/\/(?:www\.)?(via\.)?placeholder\.com/i.test(src) &&
    !/default|avatar|person\.png/i.test(src);

  return (
    <div className="relative h-10 w-10">
      <span
        className="pointer-events-none absolute -inset-1 rounded-xl
                   bg-gradient-to-tr from-fuchsia-300/30 via-sky-300/25 to-emerald-300/30
                   blur-lg animate-pulse"
      />
      {isValidSrc ? (
        <img
          src={src}
          alt={name || "Profile"}
          onError={(e) => {
            e.currentTarget.style.display = "none";
            e.currentTarget.insertAdjacentHTML(
              "afterend",
              `<div class='relative h-10 w-10 rounded-xl bg-neutral-100 text-gray-700 
                       grid place-items-center text-sm font-semibold ring-1 ring-black/5'>
                 ${initials}
               </div>`
            );
          }}
          className="relative h-10 w-10 rounded-xl object-cover ring-1 ring-black/5"
        />
      ) : (
        <div className="relative h-10 w-10 rounded-xl bg-neutral-100 text-gray-700 
                        grid place-items-center text-sm font-semibold ring-1 ring-black/5">
          {initials}
        </div>
      )}
    </div>
  );
};


const CheckpointModalAdmin = ({ checkpoint, onClose, isAdmin = false, teamMembers = [], submissions = [] }) => {
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [hasPickedUp, setHasPickedUp] = useState(false);

  console.log(" Matching logic check:", {
  clickedDate: checkpoint.clickedDate,
  checkpointId: checkpoint.checkpointId,
  allSubmissionIds: submissions.map(s => s.checkpointId),
});

  // Match exactly how ProjectCalendar formats day keys
  const toLocalDateKey = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Filter submissions for this checkpoint & clicked date
  const submissionsForThisCheckpoint = submissions?.filter((s) => {
    const submittedDate = toLocalDateKey(s?.dateSubmittedFor);
    const clickedDate = toLocalDateKey(checkpoint?.clickedDate);
    return s?.checkpointId === checkpoint?.checkpointId && submittedDate === clickedDate;
  });

  console.log("🧩 Submission filter check:", {
    clickedDate: checkpoint?.clickedDate,
    allDates: submissions.map(s => s.dateSubmittedFor),
    matched: submissionsForThisCheckpoint.map(s => ({
      id: s.id,
      checkpointId: s.checkpointId,
      dateSubmittedFor: s.dateSubmittedFor
    }))
  });

  // Format submitted date for display (just pick the first one for convenience)
  const submissionDateDisplay =
    submissionsForThisCheckpoint?.length > 0
      ? new Date(submissionsForThisCheckpoint[0].dateSubmittedFor).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : null;


  // Submission state
  const [isSubmissionOpen, setSubmissionOpen] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Hover states for tooltips
  const [hoverPickup, setHoverPickup] = useState(false);
  const [hoverSubmission, setHoverSubmission] = useState(false);

  const [isTransitionOpen, setTransitionOpen] = useState(false);

  const [isFeedbackOpen, setFeedbackOpen] = useState(false);

  // temp holder for text until feedback
  const [pendingSubmission, setPendingSubmission] = useState(null);


  const [assignedTo, setAssignedTo] = useState(
    Array.isArray(checkpoint.assignedTo)
      ? checkpoint.assignedTo
      : checkpoint.assignedTo
      ? [checkpoint.assignedTo]
      : []
  );
  if (!checkpoint) return null;

  // ✅ Status
  const toUTCKey = (date) => new Date(date).toISOString().split("T")[0];
  const nowKey = toUTCKey(new Date());
  const startKey = toUTCKey(checkpoint.startDate);
  const endKey = toUTCKey(checkpoint.endDate);

  let checkpointStatus = "";
  if (nowKey > endKey) checkpointStatus = "expired";
  else if (nowKey < startKey) checkpointStatus = "upcoming";
  else checkpointStatus = "active";

  const colorMap = {
    active: "text-green-600 bg-green-50 border-green-200",
    upcoming: "text-blue-600 bg-blue-50 border-blue-200",
    expired: "text-red-600 bg-red-50 border-red-200",
  };
  const dotMap = {
    active: "bg-green-500",
    upcoming: "bg-blue-500",
    expired: "bg-red-500",
  };
  const labelMap = {
    active: "Active",
    upcoming: "Upcoming",
    expired: "Expired",
  };

  const statusColor = colorMap[checkpointStatus];
  const dotColor = dotMap[checkpointStatus];
  const statusLabel = labelMap[checkpointStatus];
  const isExpired = checkpointStatus === "expired";

  // ✅ Members list
  const uniqueMembers = Object.values(
    teamMembers.reduce((acc, member) => {
      const idKey = member.uid || member.email;
      if (!acc[idKey]) acc[idKey] = { ...member, roles: [] };
      if (member.role && !acc[idKey].roles.includes(member.role))
        acc[idKey].roles.push(member.role);
      return acc;
    }, {})
  );

  const toggleSelect = (member) => {
    if (isExpired) return;
    const idKey = member.uid || member.email;
    setSelectedMembers((prev) => {
      const exists = prev.find((m) => (m.uid || m.email) === idKey);
      return exists ? prev.filter((m) => (m.uid || m.email) !== idKey) : [...prev, member];
    });
  };

  const clearMember = (idKey) =>
    setSelectedMembers((prev) => prev.filter((m) => (m.uid || m.email) !== idKey));

  const getDisplayName = (m) =>
    m?.firstName || m?.lastName
      ? `${m.firstName || ""} ${m.lastName || ""}`.trim()
      : m?.email?.split("@")[0] || "Unknown";

  const startDisplay = new Date(checkpoint.startDate).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const endDisplay = new Date(checkpoint.endDate).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const handleAssign = async () => {
    if (!selectedMembers.length) return alert("Select at least one member first.");
    if (isExpired) return alert("🚫 Cannot assign to expired checkpoints.");

    const token = localStorage.getItem("token");
    const projectId =
      checkpoint.projectId || window.location.pathname.split("/projects/")[1].split("/")[0];

    const successfullyAssigned = [];

    for (const member of selectedMembers) {
      const encodedEmail = encodeURIComponent(member.email);
      const uidRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/user/${encodedEmail}/get-uid`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const uidData = await uidRes.json();
      if (!uidRes.ok) {
        alert(`❌ Failed to fetch UID for ${member.firstName || member.email}`);
        continue;
      }

      const body = {
        phaseName: checkpoint.phase,
        checkpointIndex: checkpoint.index,
        memberId: uidData.uid,
      };

      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/roadmaps/${projectId}/admin/assign-checkpoint`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        if (data.message?.includes("already assigned")) {
          alert(
            `⚠️ ${member.firstName || member.email.split("@")[0]} is already assigned to this checkpoint.`
          );
          // ⛔ Block: do not add or continue assigning others if duplicate
          return; // stop the entire assignment flow immediately
        } else {
          alert(`❌ ${data.message || "Assignment failed."}`);
          continue;
        }
      }

      successfullyAssigned.push(member);
    }

    if (successfullyAssigned.length > 0) {
      setAssignedTo((prev) => [...prev, ...successfullyAssigned]);
      setSelectedMembers([]);
      alert(`✅ Assigned to ${successfullyAssigned.length} member(s) successfully.`);
    }
  };

  const handlePickUp = async () => {
    try {
      const token = localStorage.getItem("token");
      const projectId =
        checkpoint.projectId || window.location.pathname.split("/projects/")[1].split("/")[0];

      const body = {
        phaseName: checkpoint.phase,
        checkpointIndex: checkpoint.index,
      };

      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/roadmaps/${projectId}/admin/pick-up`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        }
      );

      const data = await res.json();
      if (!res.ok) {
        if (data.message?.includes("already picked up")) {
          alert("⚠️ You have already picked up this checkpoint.");
          return;
        }
        throw new Error(data.message || "Failed to pick up checkpoint.");
      }

      alert("✅ You successfully picked up this checkpoint!");
      setHasPickedUp(true);
    } catch (error) {
      console.error("❌ Error picking up checkpoint:", error);
      alert("Something went wrong while picking up the checkpoint.");
    }
  };

  
  // phase 1 — user writes description
  const handleSubmitDescription = (data) => {
    setPendingSubmission({ description: data.description });
    setSubmissionOpen(false);
    setTransitionOpen(true);
  };

  // phase 2 — after transition
  const handleContinueToFeedback = () => {
    setTransitionOpen(false);
    setFeedbackOpen(true);
  };

  // phase 3 — user writes feedback → send full submission
  const handleSubmitFeedback = async (feedbackData) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");

      let roadmapId = checkpoint?.roadmapId;
      if (!roadmapId && checkpoint?.projectId) {
        const roadmapRes = await fetch(`${BACKEND_URL}/roadmaps/${checkpoint.projectId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const roadmapData = await roadmapRes.json();
        roadmapId = roadmapData?.roadmaps?.[0]?.id;
      }
      if (!roadmapId) throw new Error("Missing roadmapId");

      const submissionPayload = {
        description: pendingSubmission?.description,
        feedback: feedbackData,
        progressDelta: 0.1,
        hours: checkpoint.hours || 1,
        dateSubmittedFor: checkpoint.clickedDate || null,
      };


      const res = await fetch(`${BACKEND_URL}/submissions/${roadmapId}/${checkpoint.phase}-${checkpoint.index}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(submissionPayload),
      });

      const result = await res.json();
      if (!res.ok) throw new Error(result.message || "Submission failed");

      console.log("✅ Submission + Feedback recorded:", result);
      setFeedbackOpen(false);
      setPendingSubmission(null);
      alert("✅ Submission + Feedback recorded successfully!");
    } catch (err) {
      console.error("❌ Error submitting full submission:", err);
      alert("Failed to record submission: " + err.message);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
      >
        {/* === MODAL CONTAINER === */}
        <motion.div
          initial={{ scale: 0.92, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.92, opacity: 0 }}
          transition={{ type: "spring", stiffness: 180, damping: 22 }}
          className="relative flex flex-col md:flex-row w-full max-w-4xl 
                    bg-white/80 backdrop-blur-xl border border-white/40 
                    shadow-[0_8px_30px_rgba(0,0,0,0.1)] rounded-2xl overflow-hidden"
        >
          {/* === CLOSE BUTTON === */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-gray-400 hover:text-black transition"
          >
            <X size={22} />
          </button>

          {/* === LEFT PANEL === */}
          <div className="md:w-1/2 w-full p-8 border-r border-white/30 flex flex-col justify-center relative">
            {/* 🧑‍💻 Assigned + Picked-Up Avatars */}
            {(assignedTo?.length > 0 || checkpoint.pickedUpBy?.length > 0) && (
              <div className="absolute top-6 right-8 flex -space-x-2">
                {/* Assigned To Members (Blue ring) */}
                {(assignedTo || []).map((m, i) => (
                  <div
                    key={`assigned-${i}`}
                    className="relative hover:scale-105 transition-transform"
                    title={`Assigned: ${m.firstName || ""} ${m.lastName || m.email}`}
                  >
                    <div className="relative">
                      <Avatar
                        name={`${m.firstName || ""} ${m.lastName || ""}`}
                        src={m.profileImage}
                      />
                      <span className="absolute -bottom-1 -right-1 h-3 w-3 bg-blue-500 border-2 border-white rounded-full" />
                    </div>
                  </div>
                ))}

                {/* Picked-Up Members (Green ring) */}
                {(checkpoint.pickedUpBy || []).map((m, i) => (
                  <div
                    key={`pickup-${i}`}
                    className="relative hover:scale-105 transition-transform"
                    title={`Picked Up: ${m.firstName || ""} ${m.lastName || m.email}`}
                  >
                    <div className="relative">
                      <Avatar
                        name={`${m.firstName || ""} ${m.lastName || ""}`}
                        src={m.profileImage}
                      />
                      <span className="absolute -bottom-1 -right-1 h-3 w-3 bg-emerald-500 border-2 border-white rounded-full" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center gap-3 mb-3">
              <CheckCircle2 className="text-blue-500" size={22} />
              <h2 className="text-lg font-semibold text-gray-900 tracking-tight">
                {checkpoint.phase} Phase
              </h2>
            </div>

            <p className="text-gray-600 text-sm leading-relaxed mb-4">{checkpoint.text}</p>

            {/* Status Tag */}
            <div
              className={`inline-flex items-center gap-2 px-3 py-[3px] text-xs font-medium rounded-full border ${statusColor}`}
            >
              <span className={`w-2 h-2 rounded-full ${dotColor}`}></span>
              {statusLabel} Checkpoint
            </div>

            <div className="grid grid-cols-2 gap-y-3 text-sm text-gray-700 mt-6">
              <div className="flex items-center gap-2 text-gray-500">
                <CalendarDays size={16} />
                <span>Start</span>
              </div>
              <span>{startDisplay}</span>

              <div className="flex items-center gap-2 text-gray-500">
                <CalendarDays size={16} />
                <span>End</span>
              </div>
              <span>{endDisplay}</span>

                <div className="flex items-center gap-2 text-gray-500">
                  <CalendarDays size={16} />
                  <span>Selected</span>
                </div>

                {submissionDateDisplay && (
                  <>
                    <div className="flex items-center gap-2 text-gray-500">
                      <CalendarDays size={16} />
                      <span>Submitted</span>
                    </div>
                    <span className="text-emerald-600 font-medium">{submissionDateDisplay}</span>
                  </>
                )}

                <span>
                  {checkpoint.clickedDate
                    ? (() => {
                        const [year, month, day] = checkpoint.clickedDate.split("-");
                        const localDate = new Date(year, month - 1, day); // Force local time
                        return localDate.toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        });
                      })()
                    : "—"}
                </span>

              <div className="flex items-center gap-2 text-gray-500">
                <Clock3 size={16} />
                <span>Hours</span>
              </div>
              <span>{checkpoint.hours}</span>

              <span className="text-gray-500">Week</span>
              <span>{checkpoint.week}</span>
            </div>
          </div>

          {/* === RIGHT PANEL (ADMIN) === */}
          {isAdmin && (
            <div className="md:w-1/2 w-full p-8 flex flex-col justify-between bg-gradient-to-br from-slate-50/60 to-white/70">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <UserPlus2 size={18} className="text-gray-700" />
                  <p className="text-sm font-medium text-gray-700">Assign Members</p>
                </div>

                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-800 mb-2">Forum</h3>
                  <div className="border border-white/30 rounded-xl p-3 bg-white/60 
                                  max-h-56 overflow-y-auto overflow-x-hidden
                                  isolate">
                    <SubmissionsForum submissions={submissionsForThisCheckpoint} />
                  </div>

                </div>


                {isExpired ? (
                  <div className="text-sm text-gray-500 italic py-6 text-center">
                    🚫 This checkpoint has expired. Assignment is locked.
                  </div>
                ) : (
                  <div className="grid grid-cols-5 gap-3 max-h-44 overflow-y-auto mb-5">
                    {uniqueMembers.length > 0 ? (
                      uniqueMembers.map((member) => {
                        const idKey = member.uid || member.email;
                        const isSelected = selectedMembers.some(
                          (m) => (m.uid || m.email) === idKey
                        );
                        const fullName = `${member.firstName || ""} ${member.lastName || ""}`.trim();
                        return (
                          <motion.div
                            key={idKey}
                            whileHover={{ scale: 1.08 }}
                            whileTap={{ scale: 0.96 }}
                            onClick={() => toggleSelect(member)}
                            className={`cursor-pointer flex flex-col items-center justify-center p-2 rounded-xl border
                              transition-all duration-150 ${
                                isSelected
                                  ? "border-blue-500 bg-blue-50"
                                  : "border-gray-200 hover:bg-gray-50"
                              }`}
                          >
                            <Avatar name={fullName} src={member.profileImage} />
                            <span className="text-[11px] text-gray-700 mt-1 font-medium text-center truncate w-14">
                              {member.firstName || member.email.split("@")[0]}
                            </span>
                            {member.roles?.length > 1 && (
                              <span className="text-[10px] text-gray-500 text-center mt-0.5 w-16 truncate">
                                {member.roles.join(" / ")}
                              </span>
                            )}
                          </motion.div>
                        );
                      })
                    ) : (
                      <p className="text-gray-400 text-sm text-center col-span-5 py-4">
                        No team members found
                      </p>
                    )}
                  </div>
                )}

                {selectedMembers.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-5">
                    {selectedMembers.map((member) => {
                      const idKey = member.uid || member.email;
                      const rolesDisplay = Array.isArray(member.roles)
                        ? member.roles.join(" / ")
                        : member.role || "Member";
                      return (
                        <div
                          key={idKey}
                          className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full"
                        >
                          <Avatar
                            name={`${member.firstName || ""} ${member.lastName || ""}`}
                            src={member.profileImage}
                          />
                          <span>
                            {getDisplayName(member)}
                            <span className="text-gray-500 ml-1">({rolesDisplay})</span>
                          </span>
                          <button
                            onClick={() => clearMember(idKey)}
                            className="text-blue-500 hover:text-red-500 transition text-xs"
                          >
                            ✕
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 rounded-xl bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition"
                >
                  Close
                </button>
                <button
                  onClick={handleAssign}
                  disabled={!selectedMembers.length || isExpired}
                  className="px-5 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 
                            text-white text-sm font-medium hover:from-blue-700 hover:to-indigo-700 
                            transition disabled:opacity-40"
                >
                  Assign
                </button>
              </div>
            </div>
          )}
        </motion.div>

        {/* === FLOATING SUBMISSION BUTTON (BOTTOM RIGHT, LEFT OF PICK-UP) === */}
        <SubmissionButton
          onClick={() => setSubmissionOpen(true)}
          hasSubmitted={hasSubmitted}
          onMouseEnter={() => setHoverSubmission(true)}
          onMouseLeave={() => setHoverSubmission(false)}
        />

        <SubmissionModal
          isOpen={isSubmissionOpen}
          onClose={() => setSubmissionOpen(false)}
          onSubmit={handleSubmitDescription}
        />

        <TransitionModal
          isOpen={isTransitionOpen}
          onClose={() => setTransitionOpen(false)}
          onContinue={handleContinueToFeedback}
        />

        <FeedbackModal
          isOpen={isFeedbackOpen}
          onClose={() => setFeedbackOpen(false)}
          onSubmit={handleSubmitFeedback}
        />


        {/* === SUBMISSION TOOLTIP === */}
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          whileHover={{ opacity: 1, y: 0 }}
          className="fixed bottom-24 right-24 px-3 py-1 bg-gray-800/90 text-white text-xs rounded-md shadow-md"
        >
          Submit Progress
        </motion.div>

        {/* === FLOATING PICK-UP BUTTON (BOTTOM RIGHT) === */}
        <motion.button
          onClick={handlePickUp}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.92, rotate: -6 }}
          animate={hasPickedUp ? { scale: [1, 1.2, 1], rotate: [0, -8, 0] } : {}}
          transition={{ type: "spring", stiffness: 200, damping: 15 }}
          onMouseEnter={() => setHoverPickup(true)}
          onMouseLeave={() => setHoverPickup(false)}
          className={`fixed bottom-8 right-8 z-[10000] flex items-center justify-center
                      h-14 w-14 rounded-2xl shadow-lg backdrop-blur-md border border-white/40
                      transition-all duration-200 cursor-pointer ${
                        hasPickedUp
                          ? "bg-gradient-to-br from-emerald-500 via-green-500 to-lime-400"
                          : "bg-gradient-to-br from-emerald-400 via-emerald-500 to-teal-500 hover:shadow-emerald-400/40"
                      }`}
        >
          <motion.svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-7 w-7"
            animate={
              hasPickedUp
                ? { rotate: -15, y: 1, x: -1, pathLength: [0.9, 1, 0.95] }
                : { rotate: 0, y: 0, x: 0, pathLength: 1 }
            }
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            {/* hand icon */}
            <path d="M7 11v2a5 5 0 0 0 10 0v-1.5" />
            <path d="M12 4v5" />
            <path d="M9 6v3" />
            <path d="M15 5v4" />
          </motion.svg>

          {/* subtle pulse aura when holding */}
          {hasPickedUp && (
            <motion.span
              layoutId="aura"
              initial={{ opacity: 0 }}
              animate={{ opacity: [0.4, 0.7, 0.4], scale: [1, 1.15, 1] }}
              transition={{
                repeat: Infinity,
                duration: 2.5,
                ease: "easeInOut",
              }}
              className="absolute inset-0 rounded-2xl bg-emerald-400/40 blur-md"
            />
          )}
        </motion.button>

        {/* === TOOLTIP === */}
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          whileHover={{ opacity: 1, y: 0 }}
          className="fixed bottom-24 right-6 px-3 py-1 bg-gray-800/90 text-white text-xs rounded-md shadow-md"
        >
          Pick up Checkpoint
        </motion.div>

      </motion.div>
    </AnimatePresence>
  );

};

export default CheckpointModalAdmin;
